import React, { useState } from 'react';
import styled from 'styled-components/macro';

import Popup from 'components/Popup';
import Slider from 'components/Slider';
import Slide from 'components/Slider/Slide';
import Button from 'components/Button';
import ScrollArea from 'components/ScrollArea';

const ToggleGallery = ({ 
    images, 
    set1Name = 'Set 1', 
    set2Name = 'Set 2', 
    className 
}) => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [activeSet, setActiveSet] = useState('set1');

    const handleImageClick = (index) => {
        setSelectedImageIndex(index);
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        setIsPopupOpen(false);
    };

    if (!images || images.length === 0) {
        return null;
    }

    // Check if any images have set2
    const hasSet2 = images.some(image => image.set_2_image);

    return (
        <Container className={className}>
            <GalleryContainer>
                <ScrollArea fadeHeight="6rem" center>
                    <Grid>
                        {images.map((image, index) => (
                            <ThumbnailContainer key={image.set_1_image?.id || 'none'} onClick={() => handleImageClick(index)}>
                                <ThumbnailWrapper>
                                    <ThumbnailImage 
                                        src={image.set_1_image?.sizes?.medium_large} 
                                        alt=""
                                    />
                                    
                                    {image.set_2_image && (
                                        <ThumbnailImage
                                            src={image.set_2_image?.sizes?.medium_large}
                                            alt=""
                                            $active={activeSet === 'set2'}
                                            $overlay
                                        />
                                    )}
                                </ThumbnailWrapper>
                            </ThumbnailContainer>
                        ))}
                    </Grid>
                </ScrollArea>
            </GalleryContainer>

            {hasSet2 && (
                <ToggleContainer>
                    <ButtonGroup>
                        <ToggleButton
                            active={activeSet !== 'set1'}
                            onClick={() => setActiveSet('set1')}
                        >
                            {set1Name}
                        </ToggleButton>
                        <ToggleButton
                            active={activeSet !== 'set2'}
                            onClick={() => setActiveSet('set2')}
                        >
                            {set2Name}
                        </ToggleButton>
                    </ButtonGroup>
                </ToggleContainer>
            )}

            <Popup isOpen={isPopupOpen} onClose={handleClosePopup}>
                <SliderContainer>
                    <Slider initialSlide={selectedImageIndex} autoplay={false}>
                        {images.map((image) => (
                            <Slide key={image.set_1_image?.id || 'none'}>
                                <SlideImage 
                                    src={image?.set_1_image?.sizes?.['2048x2048']} 
                                    alt="" 
                                />
                                
                                {image.set_2_image && (
                                    <SlideImage 
                                        src={image?.set_2_image?.sizes?.['2048x2048']} 
                                        alt="" 
                                        $active={activeSet === 'set2'}
                                        $overlay 
                                    />
                                )}
                            </Slide>
                        ))}
                    </Slider>
                    
                    {/* Toggle Buttons for Popup */}
                    {hasSet2 && (
                        <PopupToggleContainer>
                            <ButtonGroup>
                                <ToggleButton
                                    active={activeSet !== 'set1'}
                                    onClick={() => setActiveSet('set1')}
                                >
                                    {set1Name}
                                </ToggleButton>
                                <ToggleButton
                                    active={activeSet !== 'set2'}
                                    onClick={() => setActiveSet('set2')}
                                >
                                    {set2Name}
                                </ToggleButton>
                            </ButtonGroup>
                        </PopupToggleContainer>
                    )}
                </SliderContainer>
            </Popup>
        </Container>
    );
};

export default ToggleGallery;


const Container = styled.div`
    width: 100%;
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const GalleryContainer = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 3rem 3rem 6rem;
`;

const Grid = styled.div`
    min-height: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    justify-content: center;
    align-content: center;
`;

const ThumbnailContainer = styled.div`
    width: 28rem;
    cursor: pointer;
    transition: transform 0.2s ease;
    
    &:hover {
        transform: scale(1.05);
    }
`;

const ThumbnailWrapper = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 3/2;
`;

const ThumbnailImage = styled.img`
    display: block;
    position: ${props => props.$overlay ? 'absolute' : 'static'};
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    opacity: ${props => props.$overlay && !props.$active ? 0 : 1};
    transition: opacity 0.3s ease;
`;


const ToggleContainer = styled.div`
    flex: 0 0 auto;
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    // gap: 1rem;
    padding-bottom: 3rem;
`;

const SliderContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const PopupToggleContainer = styled.div`
    position: absolute;
    bottom: 3rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
`;

const ButtonGroup = styled.div`
    border-radius: 999px;
    overflow: hidden;
`;

const ToggleButton = styled(Button)`
    &:not([data-active]) {
        pointer-events: none;
        cursor: default;
    }
`;


const SlideImage = styled.img`
    display: block;
    position: ${props => props.$overlay ? 'absolute' : 'static'};
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    opacity: ${props => props.$overlay && !props.$active ? 0 : 1};
    transition: opacity 0.3s ease;
`;
